import Foco from 'react-foco'
import { Toast } from 'common/ToastMessage'
import { Alert } from 'almanac-ui/icons'
import { GenerateRequest } from 'types'
import { useCallback } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import { useUpdateAISuggestionNode } from 'hooks/queries/layers/aiSuggestionNodes/useUpdateAISuggestionNode'
import { EditorView } from 'prosemirror-view'
import { Button } from 'blaze-ui/Button'

interface Props {
  onClickOutside: () => void
}

export const FailedToastMessage = ({ onClickOutside }: Props) => (
  <Foco onClickOutside={onClickOutside}>
    <Toast.Root variant="error">
      <Toast.Icon>
        <Alert />
      </Toast.Icon>
      <Toast.Description>We could not perform this action, please try again later</Toast.Description>
    </Toast.Root>
  </Foco>
)

export const FailedToastMessageWithTryAgainContainer = ({
  queryClient,
  ...props
}: Props & { request: Required<GenerateRequest>; queryClient: QueryClient; view: EditorView }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <FailedToastMessageWithTryAgain {...props} />
    </QueryClientProvider>
  )
}

export const FailedToastMessageWithTryAgain = ({
  onClickOutside,
  request,
  view,
}: Props & { request: Required<GenerateRequest>; view: EditorView }) => {
  const { mutate, isLoading } = useUpdateAISuggestionNode({
    articleId: request.article_id,
    layerId: request.layer_id,
    id: request.uuid,
  })

  const handleTryAgain = useCallback(() => {
    mutate(
      { prompt: request.prompt },
      {
        onSuccess: (data) => {
          onClickOutside()
          view.state.doc.descendants((node, pos) => {
            if (node.attrs.blockId === request.uuid) {
              const tr = view.state.tr
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, aiSuggestionRequestId: data.node.request_id })
              view.dispatch(tr)
            }
            return true
          })
        },
      },
    )
  }, [view, mutate, onClickOutside, request.prompt, request.uuid])

  return (
    <Foco onClickOutside={onClickOutside}>
      <Toast.Root variant="error">
        <Toast.Icon>
          <Alert />
        </Toast.Icon>
        <Toast.Description>We could not perform this action</Toast.Description>
        <Toast.Action>
          <div style={{ borderRadius: '8px' }}>
            <Button variant="tertiary" size="md" onPress={handleTryAgain} isDisabled={isLoading}>
              Try Again
            </Button>
          </div>
        </Toast.Action>
      </Toast.Root>
    </Foco>
  )
}
